import { Modal } from 'bootstrap';

import { getCookie, setCookie } from '../helpers/cookies';
import { addClass, removeClass, stripHtml } from '../helpers/generic';

declare global {
    interface Window {
        Loader: any;
        SMap: SMap;
    }
}

interface MapMarkers {
    markerLatitude: string;
    markerLongitude: string;
    markerIcon: string;
    markerTitle: string;
    markerAddress: string;
    markerGroups?: Array<string>;
}

interface MapMarkerOptions {
    title: string;
    anchor: any;
    url: null | string;
}

interface MapConfigParams {
    mapContainerId: string; // DOM ID selektor objektu, v rámci kterého má být mapa vykreslena
    defaultLongitude: number; // Longitude (zemepisna delka) vychoziho vycentrovani mapy ve stupnich jako desetinne cislo nebo retezec
    defaultLatitude: number; // Latitude (zemepisna sirka) vychoziho vycentrovani mapy ve stupnich jako desetinne cislo nebo retezec
    loadMapOnPageLoad?: boolean; // ma se mapa vykreslit okamzite? Ve vychozim stavu nikoliv, nacteni a vykresleni je spusteno manualne uzivatelem
    defaultZoomLevel?: number; // Vychozi uroven ZOOMu jako cele cislo (rozsah 1-18)
    markers: Array<MapMarkers>; //  pole objektu znacek (mist) mapy
    markerGroupFilterCookieName?: string; // nazev cookie s ulozenym nastavenim filtru skupin znacek
    txtFindRoute: string; // jazykove zavisly retezec "Najít trasu"
    useMarkerFilter: boolean; // ma se pouzivat filter mapovych znacek?
}

export class MapMapyCz {
    // dynamic properties (set via config params)
    private mapContainerId: string;
    private mapParentElement: HTMLElement;
    private loadMapOnPageLoad: boolean;
    private defaultLongitude: number;
    private defaultLatitude: number;
    private defaultZoomLevel: number;
    private markers: Array<MapMarkers>;
    private markerGroupFilterCookieName: string;
    private txtFindRoute: string;
    private useMarkerFilter: boolean;
    // static properties:
    private scriptLoaded = false; // priznak, zda se jiz nacetl externi skript
    private scriptUrl = 'https://api.mapy.cz/loader.js'; // URL externiho skriptu
    private script: HTMLScriptElement; // DOM objekt s vlozenym skriptem (bude vytvoren pri vlozeni skriptu do stranky)
    private map: SMap; // API objekt s mapou
    private markerLayer: SMap.Layer.Marker; // API objekt s vrstvou obsahujici znacky
    private routeLayer: SMap.Layer.Geometry; // objekt s vrstvou vykreslujici naplanovanou trasu
    private mapContainerSelector = '.map__container'; // selector map containeru
    private loadMapBtnSelector = '.btn-load-map'; // selektor load buttonu pro nacteni mapy
    private fromElementSelector = '.route-from'; // selektor formularoveho prvku s pocatecnim bodem trasy
    private toElementSelector = '.route-to'; // selektor formularoveho prvku s cilovym bodem trasy
    private routeModalSelector = '.route-modalForm'; // selektor modalniho okna s formularem pro planovani trasy
    private routeFormSelector = '.route-form'; // selektor route formu v modalnim okne
    private routeResetBtn = '.btn-route-reset'; // selektor reset/close buttonu v modalnim okne
    private mapCanvasSelector = '.map__canvas'; // selektor elementu, do ktereho se ma mapa vyrenderovat
    private mapContentSelector = '.map__content'; // selector elementu s alternativnim obsahem (odkazy)
    private externalMapLinksSelector = '.external-map'; // selector odkazu na externi mapy (buttony)
    private mapMarkerGroupFilterSelector = '.marker-group-filter'; // selector elementu s filtrem skupin znacek
    private mapMarkerGroupFilterCheckboxesSelector = '#map-group-filter input[name=mapMarkerGroups]'; // selector jednotlivych checkboxu filtru
    private mapMarkers: Array<SMap.Marker> = []; // pole znacek na mape
    private mapMarkersBounds: Array<SMap.Coords> = []; // souradnice znacek mapy (bounds)

    constructor(params: MapConfigParams) {
        this.setConfig(params);
    }

    private setConfig = (params: MapConfigParams): void => {
        if (!params) {
            throw { name: 'ArgumentNullException', message: 'Argument param cannot be null', stack: new Error().stack };
        }

        this.mapContainerId = params.mapContainerId;
        this.mapParentElement = this.getParentElement();
        this.loadMapOnPageLoad = params.loadMapOnPageLoad || false;
        this.defaultLongitude = params.defaultLongitude;
        this.defaultLatitude = params.defaultLatitude;
        this.defaultZoomLevel = params.defaultZoomLevel || 16;
        this.markers = params.markers;
        this.markerGroupFilterCookieName = params.markerGroupFilterCookieName || 'mapFilteredMarkerGroups';
        this.txtFindRoute = params.txtFindRoute;
        this.useMarkerFilter = params.useMarkerFilter || false;

        const mapContainerElements = this.mapParentElement.getElementsByClassName(
            this.getClassSelector(this.mapContainerSelector),
        );

        if (mapContainerElements && mapContainerElements.length) {
            // tlacitko pro zobrazeni mapy na vyzadani
            // nahradi staticky obrazek dynamickou mapou
            const loadMapBtnElement = this.mapParentElement.getElementsByClassName(
                this.getClassSelector(this.loadMapBtnSelector),
            )[0];
            if (loadMapBtnElement) {
                loadMapBtnElement.addEventListener('click', (e: Event) => {
                    e.preventDefault();
                    this.loadScript();
                    return false;
                });
            }

            // pridani akce a GATC eventu externim odkazum na mapy
            const mapLinks = this.mapParentElement.getElementsByClassName(
                this.getClassSelector(this.externalMapLinksSelector),
            );
            const mapLinkClickHandler = function (e: Event): false {
                e.preventDefault();
                window.open(this.href);
                return false;
            };
            if (mapLinks) {
                for (let i = 0; i < mapLinks.length; i++) {
                    mapLinks[i].addEventListener('click', mapLinkClickHandler);
                }
            }

            // nastaveni akce formulari pro vyhledani cesty
            const routeForms = this.mapParentElement.getElementsByClassName(
                this.getClassSelector(this.routeFormSelector),
            );
            if (routeForms && routeForms[0]) {
                routeForms[0].addEventListener('submit', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.setRoute(true);
                    //return false;
                });
            }

            // nastaveni akce tlacitku "close" modalu pro vyhledani cesty
            const routeResetBtns = this.mapParentElement.getElementsByClassName(
                this.getClassSelector(this.routeResetBtn),
            );
            if (routeResetBtns && routeResetBtns[0]) {
                routeResetBtns[0].addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.resetMap();
                    return false;
                });
            }

            // pokud je to nastavene, rovnou mapu loadneme (registrujeme onload callback)...
            if (this.loadMapOnPageLoad) {
                window.addEventListener('load', this.loadScript);
            }

            if (this.useMarkerFilter) {
                this.initializeMapFilter();
            }
        }
    };

    // odstrihne z CLASS selectoru prvni znak .
    private getClassSelector = (selector: string): string => {
        return selector[0] === '.' ? selector.substring(1) : selector;
    };

    // odstrihne z ID selecotru znak #
    private getIdSelector = (selector: string): string => {
        return selector[0] === '#' ? selector.substring(1) : selector;
    };

    private getParentElement = (): HTMLElement => {
        const parentElement = document.getElementById(this.getIdSelector(this.mapContainerId));
        if (!parentElement) {
            throw {
                name: 'NoParentElement',
                message: 'Map container element not found: ' + this.mapContainerId,
                stack: new Error().stack,
            };
        } else {
            return parentElement;
        }
    };

    /**
     * Funkce, ktera vlozi do stranky externi skript a po jeho nacteni zavola inicializaci mapy.
     * Pokud je jiz skript nacteni, spusti inicializaci mapy.
     */
    private loadScript = (): void => {
        console.log('loadScript', this.scriptLoaded);
        if (!this.scriptLoaded) {
            this.script = document.createElement('script');
            this.script.onload = this.handleScriptLoad;
            // @ts-ignore
            this.script.onreadystatechange = this.handleScriptReadyStateChange;
            this.script.onerror = this.handleScriptError;
            this.script.type = 'text/javascript';
            this.script.src = this.scriptUrl;
            document.body.appendChild(this.script);
        } else {
            // re-inicializace mapy
            this.initializeMap();
        }
    };

    /**
     * Pomocna funkce, ktera osetruje udalost uspesneho loadnuti skriptu.
     * Spousti pripadny callback.
     *
     */
    private handleScriptLoad = (): void => {
        if (!this.scriptLoaded) {
            this.scriptLoaded = true;
            this.scriptLoadedCallback(true);
        }
    };

    /**
     * Pomocna funkce, ktera osetruje zmeanu stavu loadovani skriptu.
     */
    private handleScriptReadyStateChange = (): void => {
        if (!this.scriptLoaded) {
            if ((this.script as any).readyState === 'complete') {
                this.handleScriptLoad();
            }
        }
    };

    /**
     * Pomocna funkce, ktera osetruje pripane selhani loadnuti skriptu.
     * Vola take callback, ale preda mu priznak znacici chybu
     *
     */
    private handleScriptError = (): void => {
        console.error('MapMapyCz: Script load failed!');
        this.scriptLoaded = false;
        this.scriptLoadedCallback(false);
    };

    /**
     * Callback spusteni pri zmene stavu loadnuti externiho skriptu (uspesne nacten/chyba pri nacitani)
     * Inicializuje mapu vcetne naseptavace a provede zmeny v UI
     *
     * @param   status    Priznak, zda byl skript uspesne nacten ci nikoliv
     */
    private scriptLoadedCallback = (status: boolean): void => {
        if (status) {
            window.Loader.async = true; // asynchronni pouziti
            window.Loader.load(null, { suggest: true }, this.initializeMap);

            const canvas = this.mapParentElement.getElementsByClassName(
                this.getClassSelector(this.mapCanvasSelector),
            )[0];
            const content = this.mapParentElement.getElementsByClassName(
                this.getClassSelector(this.mapContentSelector),
            )[0];
            const filter = this.mapParentElement.getElementsByClassName(
                this.getClassSelector(this.mapMarkerGroupFilterSelector),
            )[0];

            canvas && addClass(canvas, 'map__canvas--visible'); // TODO: zobrazeni vlastni mapy
            content && addClass(content, 'map__content--dynamic'); // zmena priznaku "stavu"
            content && removeClass(content, 'map__content--static'); // zmena priznaku "stavu"
            filter && removeClass(filter, 'marker-group-filter--disabled'); // zobrazeni filtracniho formulare
        } else {
            //TODO: nejake osetreni chyboveho stavu?
            // => nahrada mapy za staticky obrazek nebo tak neco?
        }
    };

    /**
     * Spusti vlastni inicializaci mapy na zaklade predanych parametru
     *
     */
    initializeMap = (): void => {
        // zjisteni pozice vychoziho vycentrovani mapy:
        const center = SMap.Coords.fromWGS84(this.defaultLongitude, this.defaultLatitude);

        // vyrenderovani do zvoleneho elementu a nastaveni vychoziho zoom levelu:
        //const canvas = document.querySelector(this.mapContainerId + ' ' + this.mapCanvasSelector);
        const canvas = this.mapParentElement.getElementsByClassName(this.getClassSelector(this.mapCanvasSelector))[0];
        this.map = new SMap(canvas, center, this.defaultZoomLevel);

        // nastaveni dostupnych mapovych podkladu:
        this.map.addDefaultLayer(SMap.DEF_OPHOTO);
        this.map.addDefaultLayer(SMap.DEF_OPHOTO0203);
        this.map.addDefaultLayer(SMap.DEF_OPHOTO0406);
        this.map.addDefaultLayer(SMap.DEF_TURIST);
        this.map.addDefaultLayer(SMap.DEF_HISTORIC);
        this.map.addDefaultLayer(SMap.DEF_BASE).enable();

        // nastaveni ovladace volby mapoveho podkladu:
        const layerSwitch = new SMap.Control.Layer();
        layerSwitch.addDefaultLayer(SMap.DEF_BASE);
        layerSwitch.addDefaultLayer(SMap.DEF_OPHOTO);
        layerSwitch.addDefaultLayer(SMap.DEF_TURIST);
        layerSwitch.addDefaultLayer(SMap.DEF_OPHOTO0406);
        layerSwitch.addDefaultLayer(SMap.DEF_OPHOTO0203);
        layerSwitch.addDefaultLayer(SMap.DEF_HISTORIC);
        this.map.addControl(layerSwitch, {
            left: '8px',
            top: '9px',
        });

        this.map.addDefaultControls(); // zobrazeni zakladnich ovladacich prvku
        this.map.addControl(new SMap.Control.Sync()); // aby mapa reagovala na změnu velikosti průhledu

        // mapycyMap.addDefaultContextMenu(); // zapnuti kontextoveho menu s GPS a zoomem

        // pridani nadefinovanych znacek do mapy
        this.setMapMarkers(true, true);

        // nastaveni naseptavace adresy
        const suggest = new SMap.Suggest(
            this.mapParentElement.getElementsByClassName(this.getClassSelector(this.fromElementSelector))[0],
        );
        const suggestCb = (suggestData: any): void => {
            // vyber polozky z naseptavace -> pouziti dat pro nastaveni pocatecniho bodu
            this.setFrom(
                suggestData.data.longitude,
                suggestData.data.latitude,
                suggestData.data.title + ', ' + suggestData.data.secondRow,
            );
        };
        suggest && suggest.addListener('suggest', suggestCb);

        // zakazani zoomu pomoci mysi
        let mouseControl: SMap.Control | null = null;
        const controls = this.map.getControls();
        for (let i = 0; i < controls.length; i++) {
            // je treba najit prislusny control...
            if (controls[i] instanceof SMap.Control.Mouse) {
                mouseControl = controls[i];
            }
        }
        mouseControl && (mouseControl as any).configure(SMap.MOUSE_PAN | SMap.MOUSE_ZOOM); // ...a ten upravit
    };

    /**
     * Nastavi vychozi hodnoty filtry mapovych znacek + potrebne eventy
     *
     */
    initializeMapFilter = (): void => {
        const cookie = getCookie(this.markerGroupFilterCookieName); // overeni existence nejake cookie
        const mapMarkerGroupCheckboxes = document.querySelectorAll(this.mapMarkerGroupFilterCheckboxesSelector);

        if (mapMarkerGroupCheckboxes && mapMarkerGroupCheckboxes.length) {
            mapMarkerGroupCheckboxes.forEach((checkbox: HTMLInputElement) => {
                checkbox.addEventListener('click', (e) => {
                    this.saveSelectedMarkerGroupCookie(this.getFilteredMarkerGroups()); // ulozeni hodnot filtru do cookie
                    this.setMapMarkers(false, false); // reset znacek
                });
                // zmena vychoziho nastaveni jen v pripade, ze nejaka cookie existuje:
                if (cookie) {
                    if (this.checkSelectedMarkerGroupsCookie(checkbox.value)) {
                        checkbox.setAttribute('checked', 'checked');
                    } else {
                        checkbox.removeAttribute('checked');
                    }
                }
            });
        }
    };

    /**
     * Vytvori v mape zadane znacky
     * Metodu lze volat i kdykoliv pozdeji, kdy prekresli znacky aktualnimi.
     * Napriklad tedy po zmene filtru zobrazenych skupin (nastaveni filtru bere z cookie)
     *
     * @param   centerMap          Priznak, zda se ma pri (re)inicializaci mapy provest vycentrovani mapy
     * @param   setDefaultZoom     Priznak, zda se ma pri (re)inicializaci mapy nastavit vychozi zoom level
     */
    setMapMarkers = (centerMap: boolean, setDefaultZoom: boolean): void => {
        this.mapMarkers = []; // reset znacek mapy
        this.mapMarkersBounds = []; // reset souradnic znacek mapy

        // vytvoreni markeru
        const cookieMarkerFilter = getCookie(this.markerGroupFilterCookieName); // overeni existence nejake cookie
        if (this.markers && this.markers.length) {
            for (let i = 0; i < this.markers.length; i++) {
                const marker = this.markers[i];
                if (cookieMarkerFilter) {
                    // je aktivni nejaky filtr
                    const markerGroups = marker.markerGroups;
                    if (markerGroups && !this.checkSelectedMarkerGroupsCookie(markerGroups)) {
                        continue; // preskocime tento marker, protoze neni ve filtru vybranych skupin k zobrazeni
                    }
                }

                const coords = SMap.Coords.fromWGS84(
                    parseFloat(marker.markerLongitude),
                    parseFloat(marker.markerLatitude),
                ); // ziskame souradnice
                const options: MapMarkerOptions = {
                    title: stripHtml(marker.markerTitle), // :hover title
                    anchor: {
                        // (relativni) ukotveni znacky za bod uprostred dole
                        left: 0.5,
                        bottom: 0,
                    },
                    url: null,
                };
                if (marker.markerIcon) {
                    options.url = marker.markerIcon; // specificka ikona (pokud je zadana)
                }

                const mapMarker = new SMap.Marker(coords, 'mapMarker' + i, options);

                // vytvoreni "bubliny" s popiskem
                // POZOR: vyzaduje, aby byla v miste inicializace mapy (sablone) vytvorena metoda window.mapSetToCoords ktera nastavi koordinaty do dane instance mapy
                const card = new SMap.Card();
                card.getHeader().innerHTML = '<h5>' + marker.markerTitle + '</h5>';
                card.getBody().innerHTML = '<address>' + marker.markerAddress + '</address>';
                card.getFooter().innerHTML =
                    '<p>' +
                    '   <small class="gps">' +
                    coords.toWGS84(2).reverse().join(', ') +
                    '</small>' +
                    '   <small class="gps">' +
                    coords.toWGS84(0).reverse().join(', ') +
                    '</small>' +
                    '   <a class="setAddress btn btn-primary" ' +
                    '       onClick="window.mapSetToCoords(' +
                    marker.markerLongitude +
                    ',' +
                    marker.markerLatitude +
                    ');return false;" ' +
                    '       href="javascript:void(0)" ' +
                    '       data-coords="' +
                    marker.markerLongitude +
                    ',' +
                    marker.markerLatitude +
                    '" ' +
                    '       data-bs-toggle="modal" ' +
                    '       data-bs-target="#' +
                    this.getIdSelector(this.mapContainerId) +
                    ' ' +
                    this.routeModalSelector +
                    '">' +
                    this.txtFindRoute +
                    '</a>' +
                    '</p>';
                (mapMarker as any).decorate(SMap.Marker.Feature.Card, card); // nastaveni popisku (bubliny)
                (mapMarker as any).decorate(SMap.Marker.Feature.RelativeAnchor, options); // nastaveni relativni pozice markeru vuci GPS souradnicim

                this.mapMarkers.push(mapMarker); // ulozeni markeru
                this.mapMarkersBounds.push(coords); // ulozeni jeho souradnic (pro pozdejsi vypocet zoomu a pozice mapy)
            }

            // vytvoreni vrstvy se znackami
            if (this.markerLayer) {
                // vrstva jiz existuje, je treba ji zresetovat
                this.markerLayer.removeAll(); // odebrani vseho z vrstvy
            } else {
                this.markerLayer = new SMap.Layer.Marker(); // vytvoreni nove vrstvy
                this.map.addLayer(this.markerLayer); // pridani do mapy
                this.markerLayer.enable(); // aktivace
            }

            // pridani vsech znacek do vrstvy
            for (let x = 0; x < this.mapMarkers.length; x++) {
                this.markerLayer.addMarker(this.mapMarkers[x]);
            }

            if (this.mapMarkers.length && centerMap) {
                // vycentrovani nad znackami
                const centeredMarkers = this.map.computeCenterZoom(this.mapMarkersBounds); // spocitat pozici mapy tak, aby vsechny znacky byly videt
                this.map.setCenterZoom(centeredMarkers[0] as SMap.Coords, centeredMarkers[1] as number);
            } else if (!centerMap) {
                const center = this.map.getCenter();
                center && this.map.setCenter(center);
            }

            if (setDefaultZoom) {
                this.map.setZoom(this.defaultZoomLevel);
            } else if (!setDefaultZoom) {
                // timhle si nejsem jisty, ze funguje spravne:
                const zoom = this.map.getZoom();
                zoom && this.map.setZoom(zoom);
            }

            // automaticke shlukovani znacek:
            // var clusterer = new SMap.Marker.Clusterer(MapyCzMap.map);
            // markerLayer.setClusterer(clusterer);
        }
    };

    /**
     * Nastavi vychozi misto trasy (start)
     *
     * @param   {float}     longitude   Zemepisna delka souradnic vychoziho mista trasy ve formatu WGS-84 (desetinne cislo)
     * @param   {float}     latitude    Zemepisna sirka souradnic vychoziho mista trasy ve formatu WGS-84 (desetinne cislo)
     * @param   {string}    value       Volitelny textovy zapis polohy (adresa)
     */
    setFrom = (longitude: number, latitude: number, value: string): void => {
        const elements = this.mapParentElement.getElementsByClassName(this.getClassSelector(this.fromElementSelector));
        const element = elements && elements[0];
        if (element) {
            element.setAttribute('data-latitude', `${latitude}`);
            element.setAttribute('data-longitude', `${longitude}`);
            if (value) {
                // element.attr("value", value);
                element.setAttribute('value', value);
            }
        }
    };

    /**
     * Nastavi cilove misto trasy
     *
     * @param   {float}     longitude   Zemepisna delka souradnic ciloveho mista trasy ve formatu WGS-84 (desetinne cislo)
     * @param   {float}     latitude    Zemepisna sirka souradnic ciloveho mista trasy ve formatu WGS-84 (desetinne cislo)
     * @param   {string}    value       Volitelny textovy zapis polohy (adresa)
     */
    setTo = (longitude: number, latitude: number, value?: string): void => {
        const elements = this.mapParentElement.getElementsByClassName(this.getClassSelector(this.toElementSelector));
        const element = elements && elements[0];
        if (element) {
            element.setAttribute('data-latitude', `${latitude}`);
            element.setAttribute('data-longitude', `${longitude}`);
            if (value) {
                element.setAttribute('value', value);
            }
        }
    };

    /**
     * Nastavi parametry cesty (pocatecni a cilove misto) a spusti vykresleni trasy
     *
     * @param   {boolean}   closeModal  Zavrit modalni okno s formularem pro planovani trasy?
     */
    setRoute = (closeModal: boolean): void => {
        const fromElements = this.mapParentElement.getElementsByClassName(
            this.getClassSelector(this.fromElementSelector),
        );
        const toElements = this.mapParentElement.getElementsByClassName(this.getClassSelector(this.toElementSelector));
        const fromElement = fromElements && fromElements[0];
        const toElement = toElements && toElements[0];
        if (fromElement) {
            const coords = [
                SMap.Coords.fromWGS84(
                    parseFloat(fromElement.getAttribute('data-longitude') as string),
                    parseFloat(fromElement.getAttribute('data-latitude') as string),
                ),
                SMap.Coords.fromWGS84(
                    parseFloat(toElement.getAttribute('data-longitude') as string),
                    parseFloat(toElement.getAttribute('data-latitude') as string),
                ),
            ];

            new SMap.Route(coords, this.findRoute);
            const modalElements = this.mapParentElement.getElementsByClassName(
                this.getClassSelector(this.routeModalSelector),
            );
            if (closeModal && modalElements && modalElements[0]) {
                // zavrit modal s nastavenim cesty?
                const myModal = Modal.getInstance(modalElements[0]);
                myModal && myModal.hide();
            }
        }
    };

    /**
     * Spusti vyhledani trasy a vyrenderuje na mape novou vrstvu s vysledkem
     *
     * @param   {object}    route    Vysledek planovani (viz https://api.mapy.cz/doc/SMap.Route.html)
     */
    findRoute = (route: SMap.Route): void => {
        if (this.routeLayer) {
            // odstraneni stavajicich dat
            this.routeLayer.removeAll();
            this.routeLayer.clear();
            this.routeLayer.disable();
        }

        // vytvoreni vrstvy s trasou, vlozeni do mapy a aktivace
        this.routeLayer = new SMap.Layer.Geometry('route');
        this.map.addLayer(this.routeLayer as any).enable();

        // vycentrovani a zazoomovani cele trasy
        const coords = (route.getResults() as any).geometry;
        const computedCenteredZoom = this.map.computeCenterZoom(coords);
        this.map.setCenterZoom(computedCenteredZoom[0] as SMap.Coords, computedCenteredZoom[1] as number);

        // vykresleni jednotlivych bodu trasy do vrstvy s trasou
        const geometry = new SMap.Geometry(SMap.GEOMETRY_POLYLINE, 'geometry', coords, {});
        this.routeLayer.addGeometry(geometry);
    };

    /**
     * Odstrani z mapy nakreslenou trasu a zavre otevrenou vizitku
     * TODO: nastavi vychozi hodnoty? [zvazit implementaci]
     *
     */
    resetMap = (): void => {
        if (this.routeLayer) {
            this.routeLayer.disable();
        }
        if (this.map.getCard()) {
            this.map.removeCard();
        }
    };

    /**
     *	MapyCzMap_getFilteredMarkerGroups()
     *
     * 	<p>Vrací ID vybraných skupin značek z filtru. Pokud není nic vybráno, vrací "NONE".</p>
     *
     */
    getFilteredMarkerGroups = (): string => {
        const selectedGroups = document.querySelectorAll('input[name=mapMarkerGroups]:checked');
        let ret = 'NONE';

        if (selectedGroups && selectedGroups.length) {
            const groups: Array<string> = [];
            selectedGroups.forEach((checkbox: HTMLInputElement) => {
                groups.push(checkbox.value);
            });
            ret = groups.join(',');
        }

        return ret;
    };

    /**
     *    MapyCzMap_saveSelectedMarkerGroupCookie()
     *
     *    <p>Uloží nastavení vyfiltrovaných kategorií (skupiny značek) do cookie.</p>
     *
     *
     * @param groups    seznam kategorií událostí, jinak "0"
     */
    saveSelectedMarkerGroupCookie = (groups: string): void => {
        setCookie(this.markerGroupFilterCookieName, groups, 60);
    };

    /**
     *    MapyCzMap_checkSelectedMarkerGroupsCookie()
     *
     *    <p>Ověří, zda je uvedená skupina značek uložena v cookie s nastaveným filtrem.</p>
     *
     *
     *  @returns    (boolean)
     *
     * @param markerGroups  pole s id kategorii dane znacky
     */
    checkSelectedMarkerGroupsCookie = (markerGroups: Array<string> | string): boolean => {
        const groupsCookie = getCookie(this.markerGroupFilterCookieName);
        const groups = groupsCookie ? groupsCookie.split(',') : [];
        if (markerGroups && groups) {
            let markerGroupsArray: Array<string> = [];
            if (typeof markerGroups === 'string') {
                markerGroupsArray.push(markerGroups);
            } else if (typeof markerGroups === 'object') {
                markerGroupsArray = markerGroups;
            }
            for (let i = 0; i < markerGroupsArray.length; i++) {
                if (groups.indexOf(markerGroupsArray[i]) > -1) {
                    return true;
                }
            }
        }
        return false;
    };
}
